import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Callout,
  Caption,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type AccentName,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, SPLIT, ZERO, BUG1, BUG2, BUG3, BUG4, PUNCH, VERDICT, LESSON, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
      textAlign: "center",
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({ children, size = 92 }) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 1.02,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1620,
    }}
  >
    {children}
  </div>
);

const Sub: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 26,
      color: COLORS.dim,
      marginTop: 28,
      textAlign: "center",
      maxWidth: 1440,
    }}
  >
    {children}
  </div>
);

/**
 * The recurring beat of this episode: a reading MOVED, and the thing that moved it
 * was the instrument, not the product. `from` is struck through, `to` is the accent.
 */
const ReadingStrip: React.FC<{
  note: string;
  from: string;
  to: string;
  accent: AccentName;
  style?: React.CSSProperties;
}> = ({ note, from, to, accent, style }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 22,
      border: `1px solid ${COLORS[accent]}55`,
      background: COLORS.panel,
      borderRadius: 10,
      padding: "12px 28px",
      boxShadow: `0 0 30px -18px ${COLORS[accent]}`,
      ...style,
    }}
  >
    <span
      style={{
        fontFamily: FONTS.mono,
        fontSize: 19,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color: COLORS.dim,
      }}
    >
      {note}
    </span>
    <span
      style={{
        fontFamily: FONTS.display,
        fontWeight: 700,
        fontSize: 38,
        color: COLORS.dim,
        textDecoration: "line-through",
      }}
    >
      {from}
    </span>
    <span style={{ fontFamily: FONTS.mono, fontSize: 26, color: COLORS.dim }}>→</span>
    <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 52, color: COLORS[accent] }}>{to}</span>
  </div>
);

const fadeIn = (frame: number, a: number, b: number) =>
  interpolate(frame, [a, b], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

/* 01 — HOOK: the callback to ep 43's closing promise, then the real shape of the story */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const head = fadeIn(frame, 70, 100);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="last episode · the promise" color={COLORS.cyan} />
          <Panel accent="cyan" title="how episode 43 ended" width={1180}>
            <div
              style={{
                fontFamily: FONTS.body,
                fontSize: 30,
                lineHeight: 1.5,
                color: COLORS.dim,
                fontStyle: "italic",
              }}
            >
              &ldquo;a quality layer that&rsquo;s live, reachable, and on by default,{" "}
              <span style={{ color: COLORS.text }}>waiting to be measured</span>.&rdquo;
              <br />
              <span style={{ color: COLORS.cyan, fontStyle: "normal", fontFamily: FONTS.mono, fontSize: 24 }}>
                ▸ proving it comes next
              </span>
            </div>
          </Panel>
          <div style={{ opacity: head, marginTop: 34 }}>
            <Big size={68}>
              This is that measurement, and it was{" "}
              <span style={{ color: COLORS.red }}>wrong four times in one day</span>.
            </Big>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — THE JOB: retriever's fault vs generator's fault, and what each implies */
export const Scene02Split: React.FC = () => {
  const frame = useCurrentFrame();
  const second = fadeIn(frame, 34, 62);
  const note = fadeIn(frame, 175, 205);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.split.duration} />
      <AbsoluteFill style={{ ...center, padding: "0 60px" }}>
        <Kicker text="the one number that steers the roadmap" color={COLORS.cyan} />
        <div style={{ display: "flex", gap: 34, alignItems: "stretch" }}>
          <Panel accent="red" title="retriever side" width={680}>
            <div style={{ fontFamily: FONTS.display, fontWeight: 600, fontSize: 32, color: COLORS.text, marginBottom: 14 }}>
              the source was never fetched
            </div>
            <div style={{ fontFamily: FONTS.body, fontSize: 23, lineHeight: 1.5, color: COLORS.dim }}>
              the answer never had a chance. the model was handed the wrong context.
            </div>
            <div style={{ fontFamily: FONTS.mono, fontSize: 21, color: COLORS.red, marginTop: 18 }}>
              → go build a smarter retriever
            </div>
          </Panel>
          <div style={{ opacity: second, alignSelf: "stretch" }}>
            <Panel accent="amber" title="generator side" width={680}>
              <div style={{ fontFamily: FONTS.display, fontWeight: 600, fontSize: 32, color: COLORS.text, marginBottom: 14 }}>
                it was fetched, then mishandled
              </div>
              <div style={{ fontFamily: FONTS.body, fontSize: 23, lineHeight: 1.5, color: COLORS.dim }}>
                the right source was right there in the context window, and the answer still missed it.
              </div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 21, color: COLORS.amber, marginTop: 18 }}>
                → retrieval is fine, spend the effort elsewhere
              </div>
            </Panel>
          </div>
        </div>
        <div style={{ marginTop: 34, opacity: note }}>
          <Callout label="↳ why this one number matters most" accent="cyan" width={1300}>
            it gates a big, expensive piece of <b style={{ color: COLORS.text }}>retrieval work</b> that I had already deferred once.
            get the split wrong, and the next month goes into the wrong half of the pipeline.
          </Callout>
        </div>
      </AbsoluteFill>
      <Caption lines={SPLIT} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 03 — THE READING: 0% retriever-side */
export const Scene03Zero: React.FC = () => {
  const frame = useCurrentFrame();
  const stamp = fadeIn(frame, 150, 180);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.zero.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="what the gate reported on the first run" color={COLORS.green} />
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <MetricCounter value={0} startFrame={14} durationFrames={40} accent="green" fontSize={230} suffix="%" />
          </div>
          <div
            style={{
              fontFamily: FONTS.mono,
              fontSize: 30,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: COLORS.dim,
              marginTop: 10,
            }}
          >
            of failures were the retriever&rsquo;s fault
          </div>
          <div
            style={{
              marginTop: 40,
              opacity: stamp,
              transform: `scale(${0.94 + stamp * 0.06})`,
              fontFamily: FONTS.mono,
              fontSize: 25,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: COLORS.amber,
              border: `2px solid ${COLORS.amber}`,
              padding: "10px 24px",
              borderRadius: 8,
            }}
          >
            read literally · retrieval is perfect
          </div>
          <Sub>
            and the <span style={{ color: COLORS.red }}>retrieval work dies right here</span>, before it is ever built
          </Sub>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={ZERO} accent="green" />
    </AbsoluteFill>
  );
};

/* 04 — BUG ONE: 0 of 15 cases labeled, so every failure fell through to the default */
const FALLTHROUGH: PipeNode[] = [
  { label: "golden case", value: "no label", sub: "which source should answer?", accent: "red" },
  { label: "classifier", value: "can't prove it", sub: "nothing to check against", accent: "amber" },
  { label: "fall-through", value: "conservative default", sub: "assume the model erred", accent: "amber" },
  { label: "reported as", value: "generator side", sub: "every single time", accent: "magenta" },
];
export const Scene04Bug1: React.FC = () => {
  const frame = useCurrentFrame();
  const flow = fadeIn(frame, 140, 170);
  const strip = fadeIn(frame, 300, 335);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.bug1.duration} />
      <AbsoluteFill style={{ ...center, padding: "0 70px" }}>
        <Kicker text="bug one of four · the gate could not see" color={COLORS.red} />
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            justifyContent: "center",
            maxWidth: 1500,
            marginBottom: 14,
          }}
        >
          {Array.from({ length: 15 }, (_, i) => {
            const on = interpolate(frame, [14 + i * 5, 32 + i * 5], [0.1, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            return (
              <div
                key={i}
                style={{
                  opacity: on,
                  fontFamily: FONTS.mono,
                  fontSize: 19,
                  color: COLORS.dim,
                  border: `1px solid ${COLORS.red}44`,
                  background: COLORS.panel,
                  borderRadius: 8,
                  padding: "7px 13px",
                }}
              >
                case {String(i + 1).padStart(2, "0")} <span style={{ color: COLORS.red }}>⌀ no label</span>
              </div>
            );
          })}
        </div>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 26,
            color: COLORS.red,
            marginBottom: 24,
          }}
        >
          0 of 15 golden cases said which source should have answered
        </div>
        <div style={{ width: 1740, opacity: flow }}>
          <PipelineGraph nodes={FALLTHROUGH} startFrame={145} stepFrames={24} nodeWidth={300} />
        </div>
        <div style={{ marginTop: 34, opacity: strip }}>
          <ReadingStrip note="once the cases were labelled" from="0%" to="50%" accent="amber" />
        </div>
      </AbsoluteFill>
      <Caption lines={BUG1} accent="red" />
    </AbsoluteFill>
  );
};

/* 05 — BUG TWO: the corpus probe queried a column the table does not have */
export const Scene05Bug2: React.FC = () => {
  const frame = useCurrentFrame();
  const second = fadeIn(frame, 120, 155);
  const strip = fadeIn(frame, 285, 320);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.bug2.duration} />
      <AbsoluteFill style={{ ...center, padding: "0 60px" }}>
        <Kicker text="bug two of four · a probe on a column that does not exist" color={COLORS.amber} />
        <div style={{ display: "flex", gap: 34, alignItems: "stretch" }}>
          <Panel accent="red" title="what the probe asked" width={680}>
            <div style={{ fontFamily: FONTS.display, fontWeight: 600, fontSize: 30, color: COLORS.text, marginBottom: 14 }}>
              is this source in the corpus?
            </div>
            <div style={{ fontFamily: FONTS.body, fontSize: 23, lineHeight: 1.5, color: COLORS.dim }}>
              it looked the path up in a field the table never had, so the answer came back empty every single time.
            </div>
          </Panel>
          <div style={{ opacity: second, alignSelf: "stretch" }}>
            <Panel accent="magenta" title="what that did to the maths" width={680}>
              <div style={{ fontFamily: FONTS.display, fontWeight: 600, fontSize: 30, color: COLORS.text, marginBottom: 14 }}>
                present sources read as missing
              </div>
              <div style={{ fontFamily: FONTS.body, fontSize: 23, lineHeight: 1.5, color: COLORS.dim }}>
                sources sitting in the corpus were marked{" "}
                <span style={{ color: COLORS.red }}>never ingested</span>, and never-ingested cases are dropped from the gate
                entirely.
              </div>
            </Panel>
          </div>
        </div>
        <div style={{ marginTop: 36, opacity: strip }}>
          <ReadingStrip note="once the probe read the right field" from="50%" to="66.7%" accent="cyan" />
        </div>
      </AbsoluteFill>
      <Caption lines={BUG2} accent="amber" />
    </AbsoluteFill>
  );
};

/* 06 — BUG THREE: the OTHER half of the gate was hardcoded to false */
const PATHS = ["early return", "no linked source", "the normal path"];
export const Scene06Bug3: React.FC = () => {
  const frame = useCurrentFrame();
  const strip = fadeIn(frame, 250, 290);
  const note = fadeIn(frame, 300, 340);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.bug3.duration} />
      <AbsoluteFill style={{ ...center, padding: "0 60px" }}>
        <Kicker text="bug three of four · the other half of the gate was hardcoded" color={COLORS.violet} />
        <Panel accent="violet" title="the multi-hop metric · every code path it had" width={1180}>
          {PATHS.map((p, i) => {
            const on = fadeIn(frame, 30 + i * 26, 55 + i * 26);
            return (
              <div
                key={p}
                style={{
                  opacity: on,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontFamily: FONTS.mono,
                  fontSize: 26,
                  color: COLORS.dim,
                  padding: "12px 6px",
                  borderBottom: i < PATHS.length - 1 ? `1px solid ${COLORS.line}` : "none",
                }}
              >
                <span>{p}</span>
                <span style={{ color: COLORS.red }}>returns false</span>
              </div>
            );
          })}
        </Panel>
        <div style={{ marginTop: 30, opacity: strip }}>
          <ReadingStrip note="the reading did not move, its meaning did" from="0.0%" to="0.0%" accent="violet" />
        </div>
        <div
          style={{
            marginTop: 22,
            opacity: note,
            fontFamily: FONTS.mono,
            fontSize: 22,
            color: COLORS.dim,
            textAlign: "center",
          }}
        >
          it could only ever report zero, and zero is{" "}
          <span style={{ color: COLORS.text }}>the number the gate is written on</span>
        </div>
      </AbsoluteFill>
      <Caption lines={BUG3} accent="violet" />
    </AbsoluteFill>
  );
};

/* 07 — BUG FOUR: the harness stopped at the top 5, production does not */
const RANK_HITS = [6, 10, 13];
const SLOT_W = 60;
const SLOT_GAP = 8;
const rankCenter = (r: number) => (r - 1) * (SLOT_W + SLOT_GAP) + SLOT_W / 2;

export const Scene07Bug4: React.FC = () => {
  const frame = useCurrentFrame();
  const ruler = fadeIn(frame, 110, 145);
  const hits = fadeIn(frame, 240, 285);
  const note = fadeIn(frame, 330, 370);
  const railWidth = 20 * SLOT_W + 19 * SLOT_GAP;
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.bug4.duration} />
      <AbsoluteFill style={{ ...center, padding: "0 60px" }}>
        <Kicker text="bug four of four · the harness was stricter than production" color={COLORS.amber} />
        <div style={{ display: "flex", gap: 30, marginBottom: 40 }}>
          <Panel accent="red" title="what the harness fetched" width={560}>
            <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 44, color: COLORS.red }}>top 5</div>
            <div style={{ fontFamily: FONTS.body, fontSize: 22, color: COLORS.dim, marginTop: 8 }}>
              a single fixed window, then it stopped looking
            </div>
          </Panel>
          <div style={{ opacity: fadeIn(frame, 40, 75) }}>
            <Panel accent="cyan" title="what production hands the model" width={560}>
              <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 44, color: COLORS.cyan }}>8, 15 or 20</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 22, color: COLORS.dim, marginTop: 8 }}>
                the window widens with the question being asked
              </div>
            </Panel>
          </div>
        </div>

        {/* rank rail: the harness window vs where the right answers actually sat */}
        <div style={{ position: "relative", width: railWidth, height: 108, opacity: ruler }}>
          {/* hit labels, above the rail */}
          {RANK_HITS.map((r) => (
            <div
              key={`lab-${r}`}
              style={{
                position: "absolute",
                left: rankCenter(r),
                top: 0,
                transform: "translateX(-50%)",
                opacity: hits,
                fontFamily: FONTS.mono,
                fontSize: 20,
                color: COLORS.amber,
                whiteSpace: "nowrap",
              }}
            >
              rank {r}
            </div>
          ))}
          {/* the 20 rank slots */}
          <div style={{ position: "absolute", top: 40, left: 0, display: "flex", gap: SLOT_GAP }}>
            {Array.from({ length: 20 }, (_, i) => {
              const r = i + 1;
              const inWindow = r <= 5;
              const isHit = RANK_HITS.includes(r);
              const c = isHit ? COLORS.amber : inWindow ? COLORS.cyan : COLORS.line;
              return (
                <div
                  key={r}
                  style={{
                    width: SLOT_W,
                    height: 30,
                    borderRadius: 5,
                    border: `1.5px solid ${c}`,
                    background: isHit ? `${COLORS.amber}${hits > 0.5 ? "33" : "00"}` : inWindow ? `${COLORS.cyan}18` : "transparent",
                    boxShadow: isHit ? `0 0 18px -6px ${COLORS.amber}` : "none",
                  }}
                />
              );
            })}
          </div>
          {/* the cut line, right after rank 5 */}
          <div
            style={{
              position: "absolute",
              top: 30,
              left: 5 * (SLOT_W + SLOT_GAP) - SLOT_GAP / 2,
              width: 2,
              height: 50,
              background: COLORS.red,
              boxShadow: `0 0 12px ${COLORS.red}`,
            }}
          />
          <div
            style={{
              position: "absolute",
              top: 84,
              left: 5 * (SLOT_W + SLOT_GAP) - SLOT_GAP / 2,
              transform: "translateX(-50%)",
              fontFamily: FONTS.mono,
              fontSize: 19,
              color: COLORS.red,
              whiteSpace: "nowrap",
            }}
          >
            where the harness stopped
          </div>
        </div>

        <div style={{ marginTop: 36, opacity: note }}>
          <Callout label="↳ what that scored as" accent="amber" width={1300}>
            correct answers at ranks 6, 10 and 13 were{" "}
            <b style={{ color: COLORS.text }}>counted as retrieval failures</b>, purely because the harness stopped at 5. production
            would have handed every one of them to the model.
          </Callout>
        </div>
      </AbsoluteFill>
      <Caption lines={BUG4} accent="amber" />
    </AbsoluteFill>
  );
};

/* 08 — THE PUNCH: the fourth fix changed no retrieval code at all */
export const Scene08Punch: React.FC = () => {
  const frame = useCurrentFrame();
  const pass = fadeIn(frame, 100, 140);
  const punch = fadeIn(frame, 200, 240);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.punch.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="what the honest harness finally reported" color={COLORS.cyan} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 24 }}>
          <span
            style={{
              fontFamily: FONTS.display,
              fontWeight: 700,
              fontSize: 56,
              color: COLORS.dim,
              textDecoration: "line-through",
            }}
          >
            66.7%
          </span>
          <span style={{ fontFamily: FONTS.mono, fontSize: 34, color: COLORS.dim }}>→</span>
          <MetricCounter
            value={62.5}
            startFrame={16}
            durationFrames={52}
            accent="cyan"
            fontSize={160}
            decimals={1}
            suffix="%"
          />
        </div>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 26,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: COLORS.dim,
            marginTop: 6,
          }}
        >
          of failures were the retriever&rsquo;s fault
        </div>
        <div style={{ marginTop: 30, opacity: pass }}>
          <ReadingStrip note="cases that passed the whole run" from="4 of 15" to="7 of 15" accent="green" />
        </div>
        <div style={{ marginTop: 40, opacity: punch }}>
          <Big size={54}>
            That fix changed <span style={{ color: COLORS.amber }}>no retrieval code at all</span>.
            <br />
            Production had always behaved that way.
          </Big>
        </div>
      </AbsoluteFill>
      <Caption lines={PUNCH} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 09 — THE VERDICT: it clears its bar, and the work still stays deferred */
export const Scene09Verdict: React.FC = () => {
  const frame = useCurrentFrame();
  const second = fadeIn(frame, 60, 95);
  const note = fadeIn(frame, 210, 250);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.verdict.duration} />
      <AbsoluteFill style={{ ...center, padding: "0 60px" }}>
        <Kicker text="the call the honest gate actually makes" color={COLORS.green} />
        <div style={{ display: "flex", gap: 34, alignItems: "stretch" }}>
          <Panel accent="green" title="retriever side · clears its bar" width={660}>
            <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 74, color: COLORS.green }}>62.5%</div>
            <div style={{ fontFamily: FONTS.body, fontSize: 23, lineHeight: 1.5, color: COLORS.dim, marginTop: 10 }}>
              most failures really are the retriever&rsquo;s, and that half of the reading is now trustworthy.
            </div>
          </Panel>
          <div style={{ opacity: second, alignSelf: "stretch" }}>
            <Panel accent="red" title="multi-hop · the half the gate is written on" width={660}>
              <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 74, color: COLORS.red }}>0.0%</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 23, lineHeight: 1.5, color: COLORS.dim, marginTop: 10 }}>
                so the big, expensive work stays{" "}
                <span style={{ color: COLORS.red }}>deferred</span>, on a number I can finally believe.
              </div>
            </Panel>
          </div>
        </div>
        <div style={{ marginTop: 36, opacity: note }}>
          <Callout label="↳ why a multi-hop loop would not help here" accent="green" width={1340}>
            these failures are first-hop. a multi-hop loop fixes{" "}
            <b style={{ color: COLORS.text }}>found A, A points to B, never fetched B</b>. it does nothing at all for never having
            found A.
          </Callout>
        </div>
      </AbsoluteFill>
      <Caption lines={VERDICT} accent="green" />
    </AbsoluteFill>
  );
};

/* 10 — THE LESSON, with the day's ledger underneath it */
const LEDGER: { value: string; label: string; accent: AccentName }[] = [
  { value: "0%", label: "the first reading, before any label existed", accent: "red" },
  { value: "50%", label: "bug one, the cases finally carried labels", accent: "amber" },
  { value: "66.7%", label: "bug two, the corpus probe read a real field", accent: "cyan" },
  { value: "0.0%", label: "bug three, the other half stopped being hardcoded", accent: "violet" },
  { value: "62.5%", label: "bug four, the harness stopped being stricter", accent: "green" },
];
export const Scene10Lesson: React.FC = () => {
  const frame = useCurrentFrame();
  const ledger = fadeIn(frame, 150, 185);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.lesson.duration} />
      <AbsoluteFill style={{ ...center, padding: "0 50px" }}>
        <Kicker text="what I actually learned in one day" color={COLORS.violet} />
        <Big size={62}>
          A number is not trustworthy because it{" "}
          <span style={{ color: COLORS.amber }}>computes</span>.
          <br />
          It is trustworthy when the data underneath carries what its{" "}
          <span style={{ color: COLORS.green }}>maths depends on</span>.
        </Big>
        <div style={{ display: "flex", gap: 14, marginTop: 46, opacity: ledger, alignItems: "flex-start" }}>
          {LEDGER.map((l, i) => (
            <div
              key={l.label}
              style={{
                width: 300,
                background: COLORS.panel,
                border: `1px solid ${COLORS[l.accent]}55`,
                borderRadius: 10,
                padding: "14px 16px",
                opacity: fadeIn(frame, 155 + i * 14, 180 + i * 14),
              }}
            >
              <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 40, color: COLORS[l.accent] }}>{l.value}</div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 16, lineHeight: 1.45, color: COLORS.dim, marginTop: 8 }}>
                {l.label}
              </div>
            </div>
          ))}
        </div>
      </AbsoluteFill>
      <Caption lines={LESSON} accent="violet" />
    </AbsoluteFill>
  );
};

/* 11 — OUTRO */
export const Scene11Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={70}>
          Four broken instruments in one day, and{" "}
          <span style={{ color: COLORS.amber }}>every one of them looked like a real finding</span>.
        </Big>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            letterSpacing: "0.24em",
            color: COLORS.dim,
            marginTop: 44,
          }}
        >
          EAKC · build-in-public
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
